import { useState, useEffect } from 'react';
import { Cookie, X, Shield, Check } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Link } from 'react-router-dom';

interface CookieConsentProps {
  onAccept: () => void;
}

export function CookieConsent({ onAccept }: CookieConsentProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already consented
    const hasConsented = localStorage.getItem('cookieConsent');
    if (!hasConsented) {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setIsVisible(false);
    onAccept();
  };

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'false');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-background border-t shadow-lg">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
              <Cookie className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold flex items-center gap-2">
                <Shield className="h-4 w-4" />
                We value your privacy
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                We use cookies to enhance your experience, analyze site traffic, and serve personalized content. 
                By clicking "Accept All", you consent to our use of cookies.{' '}
                <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
                {' | '}
                <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 w-full md:w-auto">
            <Button variant="outline" size="sm" onClick={() => setShowDetails(!showDetails)}>
              {showDetails ? 'Hide Details' : 'Details'}
            </Button>
            <Button variant="outline" size="sm" onClick={handleDecline}>
              Essential Only
            </Button>
            <Button size="sm" onClick={handleAccept}>
              <Check className="mr-1 h-4 w-4" />
              Accept All
            </Button>
            <button 
              onClick={() => setIsVisible(false)}
              className="p-2 hover:bg-muted rounded-full"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {showDetails && (
          <div className="mt-4 p-4 bg-muted rounded-lg text-sm">
            <h4 className="font-semibold mb-2">Cookie Details:</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Essential Cookies</p>
                  <p className="text-muted-foreground">Required for the website to function properly</p>
                </div>
                <span className="text-green-500">Always On</span>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Analytics Cookies</p>
                  <p className="text-muted-foreground">Help us improve our website by collecting anonymous data</p>
                </div>
                <span className="text-muted-foreground">Optional</span>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Marketing Cookies</p>
                  <p className="text-muted-foreground">Used to deliver personalized advertisements</p>
                </div>
                <span className="text-muted-foreground">Optional</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
